/**
 * Assembles FR-217 "Harrow" from kit.station.* modules (docs/art/sheets/derelict-hulk.md). Asset-lane owned
 * (task H4). Gameplay places `root` in render space, adds one static trimesh body from `collider`
 * (derelict-local metres) and never looks inside. P0 STUB: a coarse layout, no debris.
 *
 * PERF PF-9 (plan docs/plans/2026-10-11-perf-pass.md, D-771): with `derelict.json merged` the root holds one `LOD`.
 * Level 0 is today's module group (per-module meshes, each with its own LOD), so the near band keeps today's look and
 * per-module culling. Levels 1+ draw one merged mesh per material, each merging the modules' own level-k geometry.
 * Merged level k switches in at max over modules of (s_i + offset_i) (s_i the module's level-k distance at lodScale 1,
 * offset_i its origin's distance from the derelict origin), so no module ever shows coarser than its own LOD would;
 * the kit's hysteresis, no lodBias. The merged geometry is built once per (material library, kit) and shared by
 * every build (the kit's models live for the app; disposeBody disposes no geometry). Colliders, light groups and
 * gameplay (Rapier raycast, damage, salvage, M2's separate cuttable sections) never read the meshes.
 * PF-9 fix (W3 gate): the modules' LIGHT_ socket sprites (the mast-tip beacon) hang off the module roots, so a merged
 * level hid them with the module group. With `merged` they move to a root-level group at the same derelict-local pose
 * and draw at every distance, exactly as the per-module build draws them (same sprites, no extra draws). The merged
 * emissive mesh carries its sources' LIGHT_ lenses (the kit has one; the merged-lights test fails past MAX_LIGHTS).
 *
 * M7 S43 (additive): a restarted hulk's look. With `lights` the build carries hidden light groups (lit windows, a live
 * beacon, a docking light; no new model) as sprites sharing one material per group, and `setLook(mask)` shows the
 * groups whose bit is set. Visibility is written only when the mask changes (call it per frame freely).
 */
import { BufferGeometry, Euler, Group, type LOD, Matrix4, Mesh, Quaternion, Vector3, type Material, type Object3D, type Sprite } from 'three/webgpu'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { z } from 'zod'
import type { LoadedModel, ModelInstance } from '../../assets/loader'
import { applyStaticLook, type MaterialLibrary } from '../materials'
import { FAR, HULL_DATA_KEY, MAX_ENGINES, MAX_LIGHTS, ENGINE_KIND, type HullObjectData } from '../materials/hull/objectData'
import { createLightSprite } from '../materials/lights'
import { ViewLOD } from '../viewLod'
import derelictJson from './derelict.json'

/** derelict.json: `merged` false = today's per-module build at every distance (PF-9's revert by data). */
export const derelictFileSchema = z.object({ merged: z.boolean() }).strict()
export type DerelictFile = z.infer<typeof derelictFileSchema>
export const derelictConfig: DerelictFile = derelictFileSchema.parse(derelictJson)

/** One light group of a restarted hulk (derelict-local metres). */
export interface DerelictLightGroup {
  /** The look bit that shows it. */
  readonly bit: number
  readonly color: string
  /** A beacon's flash rate; absent or 0 = steady. */
  readonly blinkHz?: number
  readonly positions: readonly (readonly [number, number, number])[]
}

export interface DerelictBuild {
  readonly root: Object3D
  /** Static trimesh in derelict-local metres (+Y = spindle axis, -Z = the face turned toward the spawn). */
  readonly collider: { vertices: Float32Array; indices: Uint32Array }
  /** Bounding-sphere radius around the derelict origin, metres. */
  readonly radius: number
  /** M7 S43: shows the light groups whose bit is set in `mask` (a no-op without `lights` or when unchanged). */
  setLook(mask: number): void
}

interface Placement {
  id: string
  pos: [number, number, number]
  rot: [number, number, number] // radians, Euler XYZ
}

const DEG = Math.PI / 180

function layout(): Placement[] {
  const out: Placement[] = []
  const coreYs = [-62, -42, -22, -2, 18, 38, 58, 78]
  for (const y of coreYs) out.push({ id: y === -2 || y === 58 ? 'kit.station.core_breached' : 'kit.station.core', pos: [0, y, 0], rot: [0, 0, 0] })
  out.push({ id: 'kit.station.dock', pos: [0, 111, 0], rot: [0, 0, 0] })
  out.push({ id: 'kit.station.refinery', pos: [0, -92, 0], rot: [0, 0, 0] })
  for (let slot = 0; slot < 24; slot++) {
    const deg = slot * 15
    if (deg >= 165 && deg < 225) continue
    const th = deg * DEG
    const id = deg === 150 || deg === 225 ? 'kit.station.hab_broken' : 'kit.station.hab'
    out.push({ id, pos: [61.5 * Math.cos(th), 40, -61.5 * Math.sin(th)], rot: [0, Math.atan2(-Math.cos(th), Math.sin(th)), 0] })
  }
  for (const deg of [45, 135, 225, 315]) {
    const th = deg * DEG
    out.push({ id: deg === 225 ? 'kit.station.truss_broken' : 'kit.station.truss', pos: [31.5 * Math.cos(th), 40, -31.5 * Math.sin(th)], rot: [0, th, -90 * DEG] })
    out.push({ id: 'kit.station.solar', pos: [35.5 * Math.cos(th), -20, -35.5 * Math.sin(th)], rot: [90 * DEG, th, 0] })
  }
  for (const deg of [0, 120, 240]) {
    const th = deg * DEG
    out.push({ id: 'kit.station.tank', pos: [12 * Math.cos(th), -35, -12 * Math.sin(th)], rot: [0, th, 0] })
  }
  return out
}

/** The hidden light groups and the look setter (S43). */
function buildLights(lib: MaterialLibrary, root: Group, groups: readonly DerelictLightGroup[]): (mask: number) => void {
  const made: { bit: number; group: Group }[] = []
  for (const [i, g] of groups.entries()) {
    const holder = new Group()
    holder.name = `derelict:lights:${i}`
    holder.visible = false
    const first = createLightSprite(lib, `derelict:${i}`, { color: g.color, blinkHz: g.blinkHz })
    for (const [j, p] of g.positions.entries()) {
      const s = j === 0 ? first : first.clone()
      s.position.set(p[0], p[1], p[2])
      holder.add(s)
    }
    root.add(holder)
    made.push({ bit: g.bit, group: holder })
  }
  let shown = 0
  return (mask: number): void => {
    if (mask === shown) return
    shown = mask
    for (const m of made) m.group.visible = (mask & m.bit) !== 0
  }
}

/** One placed module of a build (the instance under the module group). */
interface PlacedModule {
  readonly model: LoadedModel
  readonly inst: ModelInstance
  /** The module origin's distance from the derelict origin, metres. */
  readonly offset: number
}

interface MergedMeshTemplate {
  readonly name: string
  readonly geometry: BufferGeometry
  readonly material: Material
  readonly castShadow: boolean
  readonly receiveShadow: boolean
  readonly userData: Readonly<Record<string, unknown>>
}

/** One merged far level: its switch distance and one geometry per material (shared, read-only). */
interface MergedLevel {
  readonly distance: number
  readonly meshes: readonly MergedMeshTemplate[]
}

interface MergedKit {
  readonly hysteresis: number
  readonly levels: readonly MergedLevel[]
}

/** PF-9: the merged far levels per material library, then per kit (keyed by the kit's first placed model). */
const mergedCache = new WeakMap<MaterialLibrary, WeakMap<LoadedModel, MergedKit>>()

/**
 * Merged level k's switch distance: max over modules of (s_i + offset_i) at lodScale 1 (a module without level k
 * counts its coarsest). A module is at least d - offset_i from the camera, so at d >= s_i + offset_i it already shows
 * level k itself: no module is ever drawn coarser than its own LOD would draw it.
 */
export function mergedSwitchDistance(modules: readonly { readonly distances: readonly number[]; readonly offset: number }[], k: number): number {
  let d = 0
  for (const m of modules) d = Math.max(d, m.distances[Math.min(k, m.distances.length - 1)] + m.offset)
  return d
}

/** A merged mesh's per-object hull data (derelict-local): the first source's seed and panel pitch, up to 8 lenses. */
function mergedHullData(sources: readonly Mesh[], rel: readonly Matrix4[]): HullObjectData | undefined {
  const first = sources[0].userData[HULL_DATA_KEY] as HullObjectData | undefined
  if (!first) return undefined
  const engines = new Float32Array(MAX_ENGINES * 8)
  for (let i = 0; i < MAX_ENGINES; i++) engines.set([FAR, FAR, FAR, 0, 0, 0, 1, ENGINE_KIND.none], i * 8)
  const lights = new Float32Array(MAX_LIGHTS * 4)
  for (let i = 0; i < MAX_LIGHTS; i++) lights.set([FAR, FAR, FAR, 0], i * 4)
  const v = new Vector3()
  const seen = new Set<HullObjectData>()
  let n = 0
  for (let s = 0; s < sources.length && n < MAX_LIGHTS; s++) {
    const d = sources[s].userData[HULL_DATA_KEY] as HullObjectData | undefined
    if (!d || seen.has(d)) continue
    seen.add(d)
    for (let i = 0; i < MAX_LIGHTS && n < MAX_LIGHTS; i++) {
      const o = i * 4
      if (d.lights[o + 3] === 0) continue
      v.set(d.lights[o], d.lights[o + 1], d.lights[o + 2]).applyMatrix4(rel[s])
      lights.set([v.x, v.y, v.z, d.lights[o + 3]], n * 4)
      n++
    }
  }
  return { engines, lights, eye: new Float32Array([FAR, FAR, FAR, 0]), params: first.params.slice(), localFromAsset: null }
}

/** A geometry's merge key: its attribute layout and whether it is indexed (mergeGeometries needs them equal). */
function layoutKey(g: BufferGeometry): string {
  const names = Object.keys(g.attributes).sort()
  return `${g.index ? 'i' : 'n'}:${names.map((a) => `${a}${g.getAttribute(a).itemSize}`).join(',')}`
}

/** Builds the merged far levels from one build's dressed modules, in the (unparented, identity) module group's frame. */
function buildMergedKit(moduleGroup: Group, modules: readonly PlacedModule[]): MergedKit {
  moduleGroup.updateMatrixWorld(true)
  const levelCount = Math.max(...modules.map((m) => m.inst.lod.levels.length))
  const spans = modules.map((m) => ({ distances: m.model.entry.lod.distances, offset: m.offset }))
  const levels: MergedLevel[] = []
  for (let k = 1; k < levelCount; k++) {
    const groups = new Map<string, { material: Material; sources: Mesh[]; rel: Matrix4[]; parts: BufferGeometry[] }>()
    for (const m of modules) {
      const lv = m.inst.lod.levels
      lv[Math.min(k, lv.length - 1)].object.traverse((o) => {
        const mesh = o as Mesh
        if (!mesh.isMesh || Array.isArray(mesh.material)) return
        const material = mesh.material
        const key = `${material.uuid}|${layoutKey(mesh.geometry)}`
        let g = groups.get(key)
        if (!g) {
          g = { material, sources: [], rel: [], parts: [] }
          groups.set(key, g)
        }
        const rel = mesh.matrixWorld.clone()
        const part = mesh.geometry.clone()
        part.clearGroups()
        part.applyMatrix4(rel)
        g.sources.push(mesh)
        g.rel.push(rel)
        g.parts.push(part)
      })
    }
    const meshes: MergedMeshTemplate[] = []
    for (const g of groups.values()) {
      const geometry = mergeGeometries(g.parts, false)
      for (const part of g.parts) part.dispose()
      if (!geometry) throw new Error(`derelict: the level-${k} ${g.material.name} geometries do not merge`)
      geometry.computeBoundingSphere()
      const src = g.sources[0]
      const userData: Record<string, unknown> = { ...src.userData }
      const hull = mergedHullData(g.sources, g.rel)
      if (hull) userData[HULL_DATA_KEY] = hull
      meshes.push({ name: `derelict:merged:${k}:${g.material.name}`, geometry, material: g.material, castShadow: src.castShadow, receiveShadow: src.receiveShadow, userData })
    }
    levels.push({ distance: mergedSwitchDistance(spans, k), meshes })
  }
  return { hysteresis: Math.max(...modules.map((m) => m.model.entry.lod.hysteresis)), levels }
}

/**
 * PF-9 fix: moves every module's LIGHT_ socket sprites out of the (unparented, identity) module group into one
 * root-level group at the same derelict-local pose, so the top LOD's merged levels do not hide them.
 */
function hoistSocketLights(moduleGroup: Group, modules: readonly PlacedModule[]): Group {
  moduleGroup.updateMatrixWorld(true)
  const holder = new Group()
  holder.name = 'derelict:FR-217:socket-lights'
  const sprites: Sprite[] = []
  for (const m of modules) {
    for (const [name, node] of m.inst.sockets) {
      if (!name.startsWith('LIGHT_')) continue
      for (const c of node.children) if ((c as Sprite).isSprite) sprites.push(c as Sprite)
    }
  }
  for (const s of sprites) {
    s.matrixWorld.decompose(s.position, s.quaternion, s.scale)
    holder.add(s)
  }
  return holder
}

/** The top-level LOD: level 0 the module group (today's per-module build), levels 1+ the cached merged meshes. */
function mergedLod(lib: MaterialLibrary, modules: readonly PlacedModule[], moduleGroup: Group): LOD {
  let perLib = mergedCache.get(lib)
  if (!perLib) {
    perLib = new WeakMap()
    mergedCache.set(lib, perLib)
  }
  const key = modules[0].model
  let kit = perLib.get(key)
  if (!kit) {
    kit = buildMergedKit(moduleGroup, modules)
    perLib.set(key, kit)
  }
  const lod = new ViewLOD()
  lod.name = 'derelict:FR-217:lod'
  lod.addLevel(moduleGroup, 0, kit.hysteresis)
  for (const [i, level] of kit.levels.entries()) {
    const holder = new Group()
    holder.name = `derelict:FR-217:merged:${i + 1}`
    for (const t of level.meshes) {
      const mesh = new Mesh(t.geometry, t.material)
      mesh.name = t.name
      mesh.castShadow = t.castShadow
      mesh.receiveShadow = t.receiveShadow
      mesh.userData = { ...t.userData }
      holder.add(mesh)
    }
    lod.addLevel(holder, level.distance, kit.hysteresis)
  }
  return lod
}

/** `merged` (default derelict.json) = PF-9's top-level LOD; false = today's per-module build at every distance. */
export function buildDerelict(
  lib: MaterialLibrary,
  kit: ReadonlyMap<string, LoadedModel>,
  seed: number,
  lights: readonly DerelictLightGroup[] = [],
  merged: boolean = derelictConfig.merged,
): DerelictBuild {
  void seed
  const root = new Group()
  root.name = 'derelict:FR-217'
  const moduleGroup = merged ? new Group() : root
  if (merged) moduleGroup.name = 'derelict:FR-217:modules'
  const modules: PlacedModule[] = []
  const vertices: number[] = []
  const indices: number[] = []
  const m = new Matrix4()
  const q = new Quaternion()
  const one = new Vector3(1, 1, 1)
  const v = new Vector3()
  let radius = 0
  for (const p of layout()) {
    const model = kit.get(p.id)
    if (!model) continue
    const inst = model.instantiate(1)
    applyStaticLook(lib, inst, 'first_rush', { pooledLights: false })
    inst.root.position.set(p.pos[0], p.pos[1], p.pos[2])
    inst.root.rotation.copy(new Euler(p.rot[0], p.rot[1], p.rot[2]))
    moduleGroup.add(inst.root)
    modules.push({ model, inst, offset: Math.hypot(p.pos[0], p.pos[1], p.pos[2]) })
    radius = Math.max(radius, Math.hypot(p.pos[0], p.pos[1], p.pos[2]) + model.entry.bounds.radius)
    if (model.collider?.kind !== 'trimesh') continue
    q.setFromEuler(inst.root.rotation)
    m.compose(inst.root.position, q, one)
    const base = vertices.length / 3
    const src = model.collider.vertices
    for (let i = 0; i < src.length; i += 3) {
      v.set(src[i], src[i + 1], src[i + 2]).applyMatrix4(m)
      vertices.push(v.x, v.y, v.z)
    }
    for (let i = 0; i < model.collider.indices.length; i++) indices.push(base + model.collider.indices[i])
  }
  if (merged) {
    if (modules.length > 0) {
      root.add(hoistSocketLights(moduleGroup, modules))
      root.add(mergedLod(lib, modules, moduleGroup))
    }
    else root.add(moduleGroup)
  }
  const setLook = lights.length === 0 ? (): void => undefined : buildLights(lib, root, lights)
  return { root, collider: { vertices: new Float32Array(vertices), indices: new Uint32Array(indices) }, radius, setLook }
}
